// EditEmployeeForm.jsx
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="mb-3 border">
      <div
        className=" fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

export default function EditEmployeeForm() {
  const { id } = useParams(); // 👈 employee id from URL
  const navigate = useNavigate();

  const countries = {
    India: ["Maharashtra", "Karnataka", "Gujarat", "Delhi", "Tamil Nadu"],
    "United States": ["California", "Texas", "New York", "Florida", "Illinois"],
    "United Kingdom": ["England", "Scotland", "Wales", "Northern Ireland"],
  };

  const countryCodes = {
    India: "+91",
    "United States": "+1",
    "United Kingdom": "+44",
  };

  const [formData, setFormData] = useState({});
  const [photo, setPhoto] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [activeTab, setActiveTab] = useState("comm");
  const [employees, setEmployees] = useState([]);
  const [isActive, setIsActive] = useState(true); // default active
  const navbarColor = sessionStorage.getItem("navbarColor") || "#080235";


  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token"); // or sessionStorage
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);


  // ✅ Load employee details
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const token = sessionStorage.getItem("token");

        // 1️⃣ Fetch Employee
        const empRes = await fetch(`${API_BASE_URL}/api/employees/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!empRes.ok) throw new Error("Failed to fetch employee");
        const empData = await empRes.json();

        // ✅ Set employee data
        setFormData((prev) => ({
          ...prev,
          ...empData,
        }));

        // 2️⃣ Fetch Access Info (User linked with employee)
        const accessRes = await fetch(
          `${API_BASE_URL}/api/users/employee/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (accessRes.ok) {
          const accessData = await accessRes.json();

          setFormData((prev) => ({
            ...prev,
            userId: accessData.id, // ✅ store userId for update
            giveAccess: true,
            email: accessData.email,
            password: accessData.password || "", // will be masked in UI
            roles: accessData.roles || [],
            status: accessData.status,
            manager: accessData.manager,
            remainingLeaves: accessData.remainingLeaves,
            pendingLeaveRequests: accessData.pendingLeaveRequests,
          }));
        }

        // 3️⃣ Fetch Bank Details
        const bankRes = await fetch(
          `${API_BASE_URL}/api/bank-details/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (bankRes.ok) {
          const bankData = await bankRes.json();

          setFormData((prev) => ({
            ...prev,
            bankName: bankData.bankName,
            accountNumber: bankData.accountNumber,
            ifscCode: bankData.ifscCode,
            branch: bankData.branch,
            paymentType: bankData.paymentType,
          }));
        }
      } catch (err) {
        console.error("❌ Error preloading data:", err);
      }
    };

    if (id) fetchEmployeeData();
  }, [id]);




  // ✅ Load roles, designations, departments
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/roles/all`)
      .then((res) => res.json())
      .then((data) => setRoles(data.map((r) => r.name)))
      .catch(() => setErrorMessage("Unable to load roles"));

    const token = sessionStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/designations/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) =>
        setDesignations(Array.isArray(data) ? data.map((d) => d.name || d) : [])
      );

    fetch(`${API_BASE_URL}/api/departments/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) =>
        setDepartments(Array.isArray(data) ? data.map((d) => d.name || d) : [])
      );
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/employees/active`)
      .then((res) => res.json())
      .then((data) => setEmployees(data))
      .catch(() => setErrorMessage('Unable to load employees'));
  }, []);

  // ✅ Bootstrap / icons
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // ✅ Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      let updated = { ...prev, [name]: type === "checkbox" ? checked : value };

      if (name === "sameAsPermanent" && checked) {
        updated.currStreet1 = updated.street1;
        updated.currStreet2 = updated.street2;
        updated.currCity = updated.city;
        updated.currState = updated.state;
        updated.currPostalCode = updated.postalCode;
      }
      return updated;
    });
  };


  const handleStatusChange = async (e) => {
    const newStatus = e.target.checked ? "Inactive" : "Active";

    // update local state immediately
    setFormData((prev) => ({ ...prev, status: newStatus }));

    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${formData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          status: newStatus, // ✅ ensure status is sent
        }),
      });

      if (!res.ok) {
        console.error("Failed to update:", res.status);
      } else {
        const updatedUser = await res.json();
        setFormData(updatedUser); // ✅ keep state in sync with backend
      }
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };




  const handleFileChange = (e) => setPhoto(e.target.files[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert("❌ Please fill in required fields.");
      return;
    }

    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => {
      if (
        k !== "sameAsPermanent" &&
        k !== "confirmPassword" &&
        k !== "giveAccess" &&
        k !== "manualPassword" &&
        k !== "sendNotification" &&
        k !== "selectedRole" &&
        k !== "userId" && // ✅ don't send userId with employee
        !["bankName", "accountNumber", "ifscCode", "branch", "paymentType"].includes(k) // exclude bank fields
      ) {
        data.append(k, v);
      }
    });
    if (photo) data.append("photo", photo);

    try {
      const token = sessionStorage.getItem("token");

      // 1️⃣ Update Employee
      const res = await fetch(`${API_BASE_URL}/api/employees/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });

      if (!res.ok) {
        alert("❌ Failed to update employee.");
        return;
      }

      // 2️⃣ Create or Update Access Info
      if (formData.giveAccess) {
        let finalPassword = formData.password;

        if (!formData.manualPassword) {
          finalPassword =
            formData.password && formData.password.trim() !== ""
              ? formData.password
              : Math.random().toString(36).slice(-8);
        }

        const accessPayload = {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          password: finalPassword,
          roles: formData.roles || [],
          status: formData.status,
          manager: formData.manager || null,
          remainingLeaves: formData.remainingLeaves || 0,
          pendingLeaveRequests: formData.pendingLeaveRequests || 0,
          employeeId: id, // 👈 link to employee
        };

        const method = formData.userId ? "PUT" : "POST";
        const url = formData.userId
          ? `${API_BASE_URL}/api/users/${formData.userId}`
          : `${API_BASE_URL}/api/auth/register`;

        const accessRes = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(accessPayload),
        });

        if (!accessRes.ok) {
          alert("⚠️ Employee updated, but access creation/update failed.");
        }
      }

      // 3️⃣ Update Bank Details (if provided)
      // if (formData.bankName || formData.accountNumber) {
      //   const bankPayload = {
      //     bankName: formData.bankName,
      //     accountNumber: formData.accountNumber,
      //     ifscCode: formData.ifscCode,
      //     branch: formData.branch,
      //     paymentType: formData.paymentType,
      //   };

      //   const bankRes = await fetch(
      //     `http://localhost:8080/api/bank-details/${id}`,
      //     {
      //       method: "PUT",
      //       headers: {
      //         "Content-Type": "application/json",
      //         Authorization: `Bearer ${token}`,
      //       },
      //       body: JSON.stringify(bankPayload),
      //     }
      //   );

      //   if (!bankRes.ok) {
      //     alert("⚠️ Employee updated, but bank details update failed.");
      //   }
      // }

      // 3️⃣ Create or Update Bank Details
      if (formData.bankName || formData.accountNumber) {
        const bankPayload = {
          bankName: formData.bankName,
          accountNumber: formData.accountNumber,
          ifscCode: formData.ifscCode,
          branch: formData.branch,
          paymentType: formData.paymentType,
          employeeId: id, // 👈 ensure backend links to employee
        };

        const method = formData.bankDetailId ? "PUT" : "POST";
        const url = formData.bankDetailId
          ? `${API_BASE_URL}/api/bank-details/${formData.bankDetailId}`
          : `${API_BASE_URL}/api/bank-details/${id}`;

        const bankRes = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(bankPayload),
        });

        if (!bankRes.ok) {
          alert("⚠️ Employee updated, but bank details creation/update failed.");
        }
      }


      alert("✅ Employee updated successfully!");
      navigate(`/viewemployee/${id}`);
    } catch (err) {
      console.error(err);
      alert("❌ Error updating employee.");
    }
  };



  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "110px" }}>
        <div className="mb-3">
          <h4 className="m-0">  Employee</h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button
                type="submit"
                form="employeeForm"
                className="btn btn-primary me-2"
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
            </div>
            <a
              href="/allemployee"
              className="text-primary text-decoration-none me-3"
            >
              List
            </a>
          </div>
        </div>
        <form
          id="employeeForm"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
        >
          {/* Primary Information */}
          <Section title="Primary Information">
            <div className="col-md-3">
              <label className="form-label">First Name
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                className="form-control"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Last Name
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                className="form-control"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Job Role
                <span className="text-danger">*</span>
              </label>
              <select
                name="designation"
                className="form-select"
                value={formData.designation}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {designations.map((d, i) => (
                  <option key={i}>{d}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Department
                <span className="text-danger">*</span>
              </label>
              <select
                name="department"
                className="form-select"
                value={formData.department}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {departments.map((d, i) => (
                  <option key={i}>{d}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Superviser/Manager</label>
              <select
                name="superviser"
                className="form-select"
                value={formData.superviser}
                onChange={handleChange}

              >
                <option value="">Select</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                ))}
              </select>
            </div>

          </Section>



          {/* Email / Phone */}
          <Section title="Email | Phone |">
            <div className="col-md-3">
              <label className="form-label">Work Email
                <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Country
                <span className="text-danger">*</span>
              </label>
              <select
                name="country"
                className="form-select"
                value={formData.country}
                onChange={handleChange}
                required
              >
                <option value="">Select</option>
                {Object.keys(countries).map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-auto" style={{ width: "80px" }}>
              <label className="form-label">Code</label>
              <input
                type="text"
                className="form-control"
                readOnly
                value={countryCodes[formData.country] || ""}
              />
            </div>
            <div className="col-md-3 ">
              <label className="form-label">Office Phone
                <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
                pattern="[0-9]{10}"
                required
              />
            </div>
          </Section>



          {/* Classification */}
          {/* <Section title="Classification">
          </Section> */}

          {/* Tabs */}
          <ul
            className="nav nav-tabs mb-2"
            style={{ backgroundColor: navbarColor }}
          >
            {[
              ["comm", "Communication"],
              ["hr", "Human Resources"],
              ["access", "Access"],
              // ["time", "Time Tracking"],
              // ["commission", "Commission"],
              // ["marketing", "Marketing"],
              ["leaveinfo", "Leave Info"],
              ["bank", "Bank Payment Details"],
              ["system", "System Information"],
            ].map(([id, label]) => (
              <li className="nav-item" key={id}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === id ? "active" : ""}`}
                  onClick={() => setActiveTab(id)}
                  style={{
                    color: activeTab === id ? navbarColor : "#fff",
                    backgroundColor: activeTab === id ? "#fff" : "transparent",
                    fontWeight: activeTab === id ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>


          <div className="border p-3">
            {activeTab === "comm" && <div>📩 Communication Tab Content</div>}
            {/* {activeTab === "addr" && <div>🏠 Address Tab Content</div>} */}
            {activeTab === "hr" && (
              <>
                {/* <div>👥 Human Resources Tab Content</div> */}

                {/* Primary Information */}
                <Section title="Primary Information">
                  <div className="col-md-3">
                    <label className="form-label">Personal Email
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      name="pemail"
                      className="form-control"
                      value={formData.pemail}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3 ">
                    <label className="form-label">Contact No
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      name="contact"
                      className="form-control"
                      value={formData.contact}
                      onChange={handleChange}
                      pattern="[0-9]{10}"
                      required
                    />
                  </div>

                  <div className="col-md-3 ">
                    <label className="form-label">Altenative No</label>
                    <input
                      type="tel"
                      name="alno"
                      className="form-control"
                      value={formData.alno}
                      onChange={handleChange}
                      pattern="[0-9]{10}"

                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Date of Birth
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="dob"
                      className="form-control"
                      value={formData.dob}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Marital status
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      name="mstatus"
                      className="form-select"
                      value={formData.mstatus}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      <option>Married</option>
                      <option>Unmarried</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Annivarasary Date</label>
                    <input
                      type="date"
                      name="doa"
                      className="form-control"
                      value={formData.doa}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      name="gender"
                      className="form-select"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Date of Joining
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="doj"
                      className="form-control"
                      value={formData.doj}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Date of Leaving</label>
                    <input
                      type="date"
                      name="dol"
                      className="form-control"
                      value={formData.dol}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Profile Photo
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={handleFileChange}
                      required
                    />
                  </div>
                </Section>

                <Section title="Education Information">
                  <div className="col-md-3">
                    <label className="form-label">Degree
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="degree"
                      className="form-control"
                      value={formData.degree}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">University
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="institution"
                      className="form-control"
                      value={formData.institution}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Stream</label>
                    <input
                      type="text"
                      name="fieldOfStudy"
                      className="form-control"
                      value={formData.fieldOfStudy}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      name="eduStartDate"
                      className="form-control"
                      value={formData.eduStartDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      name="eduEndDate"
                      className="form-control"
                      value={formData.eduEndDate}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Grade / Percentage</label>
                    <input
                      type="text"
                      name="grade"
                      className="form-control"
                      value={formData.grade}
                      onChange={handleChange}
                    />
                  </div>
                </Section>
                <Section title="Address Information">
                  {/* ✅ Checkbox: Same as Permanent */}
                  <div className="col-12 mb-2">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="sameAsPermanent"
                        name="sameAsPermanent"
                        className="form-check-input"
                        checked={formData.sameAsPermanent}
                        onChange={handleChange}
                      />
                      <label htmlFor="sameAsPermanent" className="form-check-label">
                        Current Address same as Permanent
                      </label>
                    </div>
                  </div>

                  {/* ✅ Permanent Address */}
                  <h6 className="fw-bold">Permanent Address</h6>
                  <div className="col-md-3">
                    <label className="form-label">Street 1
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="street1"
                      className="form-control"
                      value={formData.street1}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Street 2

                    </label>
                    <input
                      type="text"
                      name="street2"
                      className="form-control"
                      value={formData.street2}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">City
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      className="form-control"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">State
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      name="state"
                      className="form-select"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={!formData.country}
                      required
                    >
                      <option value="">Select</option>
                      {formData.country &&
                        countries[formData.country].map((st) => (
                          <option key={st}>{st}</option>
                        ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Postal Code
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      className="form-control"
                      value={formData.postalCode}
                      onChange={handleChange}
                      pattern="[0-9]{5,6}"
                      required
                    />
                  </div>

                  {/* ✅ Current Address */}
                  <h6 className="fw-bold mt-3">Current Address</h6>
                  <div className="col-md-3">
                    <label className="form-label">Street 1
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="currStreet1"
                      className="form-control"
                      value={formData.currStreet1}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Street 2</label>
                    <input
                      type="text"
                      name="currStreet2"
                      className="form-control"
                      value={formData.currStreet2}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">City
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="currCity"
                      className="form-control"
                      value={formData.currCity}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">State
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      name="currState"
                      className="form-select"
                      value={formData.currState}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent || !formData.country}
                      required
                    >
                      <option value="">Select</option>
                      {formData.country &&
                        countries[formData.country].map((st) => (
                          <option key={st}>{st}</option>
                        ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Postal Code
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="currPostalCode"
                      className="form-control"
                      value={formData.currPostalCode}
                      onChange={handleChange}
                      disabled={formData.sameAsPermanent}
                      pattern="[0-9]{5,6}"
                      required
                    />
                  </div>
                </Section>

              </>
            )}

            {activeTab === "time" && <div>⏱️ Time Tracking Tab Content</div>}
            {activeTab === "commission" && <div>💰 Commission Tab Content</div>}
            {activeTab === "marketing" && <div>📊 Marketing Tab Content</div>}

            {activeTab === "bank" && (
              <div className="p-3">
                <h5 className="mb-3">📊 Bank Payment Details</h5>

                <div className="row">
                  {/* Bank Name */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="bankName"
                      value={formData.bankName || ""}
                      onChange={handleChange}
                      placeholder="Enter bank name"
                    />
                  </div>

                  {/* Account Number */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Account Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="accountNumber"
                      value={formData.accountNumber || ""}
                      onChange={handleChange}
                      placeholder="Enter account number"
                    />
                  </div>

                  {/* IFSC Code */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">IFSC Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="ifscCode"
                      value={formData.ifscCode || ""}
                      onChange={handleChange}
                      placeholder="Enter IFSC code"
                    />
                  </div>
                </div>

                <div className="row">
                  {/* Branch Name */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Branch</label>
                    <input
                      type="text"
                      className="form-control"
                      name="branch"
                      value={formData.branch || ""}
                      onChange={handleChange}
                      placeholder="Enter branch name"
                    />
                  </div>
                </div>
              </div>
            )}


            {activeTab === "access" && (
              <div>
                <h5 className="mb-3">🔑 Access Information</h5>

                {/* Give Access Checkbox */}
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="giveAccess"
                    name="giveAccess"
                    checked={formData.giveAccess || false}
                    onChange={(e) =>
                      setFormData({ ...formData, giveAccess: e.target.checked })
                    }
                  />
                  <label className="form-check-label" htmlFor="giveAccess">
                    Give Access
                  </label>
                </div>

                {/* Access fields (always visible now) */}
                <div className="row g-3">
                  {/* Name (readonly) */}
                  <div className="col-md-4">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${formData.firstName} ${formData.lastName}`}
                      readOnly
                    />
                  </div>

                  {/* Email (readonly) */}
                  <div className="col-md-4">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      readOnly
                    />
                  </div>

                  {/* Manual Password Checkbox */}
                  <div className="col-md-3">
                    <div className="form-check mt-4">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="manualPassword"
                        name="manualPassword"
                        disabled={!formData.giveAccess}
                        checked={formData.manualPassword || false}
                        onChange={(e) =>
                          setFormData({ ...formData, manualPassword: e.target.checked })
                        }
                      />
                      <label className="form-check-label" htmlFor="manualPassword">
                        Manually Assign or Change Password
                      </label>
                    </div>
                  </div>

                  {/* Generated Password (only visible when !manualPassword) */}
                  {!formData.manualPassword && generatedPassword && (
                    <div className="col-md-3">
                      <label className="form-label">Generated Password</label>
                      <input
                        type="text"
                        className="form-control"
                        value={generatedPassword}
                        readOnly
                      />
                    </div>
                  )}

                  {/* Password + Confirm Password (only if manualPassword) */}
                  {formData.manualPassword && (
                    <>
                      <div className="col-md-3">
                        <label className="form-label">Password</label>
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          disabled={!formData.giveAccess}
                          value={formData.password}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          className="form-control"
                          disabled={!formData.giveAccess}
                          value={formData.confirmPassword || ""}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}

                  {/* Roles */}
                  <div className="col-md-3">
                    <label className="form-label">Roles</label>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select"
                        disabled={!formData.giveAccess}
                        value={formData.selectedRole || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, selectedRole: e.target.value })
                        }
                      >
                        <option value="">Select Role</option>
                        {roles.map((role, idx) => (
                          <option
                            key={idx}
                            value={role}
                            disabled={(formData.roles || []).includes(role)} // ✅ safe check
                          >
                            {role}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!formData.giveAccess}
                        onClick={() => {
                          if (
                            formData.selectedRole &&
                            !(formData.roles || []).includes(formData.selectedRole)
                          ) {
                            setFormData({
                              ...formData,
                              roles: [...(formData.roles || []), formData.selectedRole],
                              selectedRole: "",
                            });
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>

                    {/* Show added roles */}
                    {formData.roles?.length > 0 && (
                      <div className="mt-2">
                        {formData.roles.map((role, idx) => (
                          <span key={idx} className="badge bg-secondary me-2">
                            {role}
                            <button
                              type="button"
                              className="btn-close btn-close-white btn-sm ms-1"
                              aria-label={`Remove ${role}`}
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  roles: formData.roles.filter((r) => r !== role),
                                })
                              }
                            />
                          </span>
                        ))}
                      </div>
                    )}

                    {errorMessage && (
                      <small className="text-danger d-block mt-1">{errorMessage}</small>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-md-3">
                    <label className="form-label">Status</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="statusCheckbox"
                        checked={formData.status === "Active"}
                        disabled={!formData.giveAccess}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.checked ? "Active" : "Inactive",
                          }))
                        }
                      />
                      <label className="form-check-label" htmlFor="statusCheckbox">
                        {formData.status === "Active" ? "Active" : "Inactive"}
                      </label>
                    </div>
                  </div>

                </div>
              </div>
            )}



            {activeTab === "system" &&
              <div>
                <h5>⚙ System Info</h5>

                {/* ✅ Inactive Checkbox */}
                <div className="form-check mb-2 text-start">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="inactiveCheckbox"
                    checked={formData.status === "Inactive"}
                    onChange={handleStatusChange}
                  />
                  <label className="form-check-label ms-2" htmlFor="inactiveCheckbox">
                    INACTIVE
                  </label>
                </div>
              </div>
            }



          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            {/* Left: Save / Cancel buttons */}
            <div>
              <button
                type="submit"
                form="employeeForm"
                className="btn btn-primary me-2"
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}